/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMemo, useState, useEffect } from 'react';
import type { Account, SiteAccount, OwnedAccount } from '@hotjar/account';
import type { Site } from '@hotjar/site';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import {
  experimentService as experiment,
  AnonymousTarget,
  AccountTarget,
  UserTarget,
  SiteTarget,
  UserWithAnonIdTarget,
  VARIANTS,
  TrafficCoverageTarget,
} from '@hotjar/experimentation';
import { useSiteMetadata } from '@hotjar/webapp/features/Activation/SetupWizard/hooks/useSiteMetadata';
import { useCurrentUser } from '@hotjar/user';

import type { TrafficTargetAttributes } from '@hotjar/experimentation';

export interface ExperimentProps {
  variant: VARIANTS;
  inVariant1: boolean;
  inVariant2: boolean;
  inExperiment?: boolean;
  hasLoaded: boolean;
}

const DEFAULT = {
  variant: VARIANTS.control,
  inExperiment: false,
  inVariant1: false,
  inVariant2: false,
  hasLoaded: false,
};

const useExperiment = (
  experimentId: string,
  target?:
    | AccountTarget
    | UserTarget
    | SiteTarget
    | AnonymousTarget
    | UserWithAnonIdTarget
    | TrafficCoverageTarget,
  canSeeExperiment = true,
): ExperimentProps => {
  const [experimentData, setExperimentData] = useState(DEFAULT);
  const { experimentAssigned: trackExperimentAssigned } = useTrack();

  useEffect(() => {
    let mounted = true;

    if (canSeeExperiment && target) {
      (async () => {
        try {
          const { variant, inExperiment } = await experiment.getVariant(experimentId, target);
          if (!mounted) return;

          if (inExperiment) {
            trackExperimentAssigned(experimentId, target.id, variant);
          }
          setExperimentData({
            variant,
            inExperiment,
            inVariant1: variant === VARIANTS.variant1,
            inVariant2: variant === VARIANTS.variant2,
            hasLoaded: true,
          });
        } catch (error) {
          setExperimentData(DEFAULT);
        }
      })();
    }

    return () => {
      mounted = false;
    };
  }, [experimentId, target, canSeeExperiment, trackExperimentAssigned]);

  return experimentData;
};

export const useAccountExperiment = (
  experimentId: string,
  account: Account | OwnedAccount | SiteAccount | undefined,
  canSeeExperiment = true,
): ExperimentProps => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const target = useMemo(() => (account ? new AccountTarget(account) : undefined), [account?.id]);
  const experimentData = useExperiment(experimentId, target, canSeeExperiment);

  return experimentData;
};

/**
 * Future person, this function has been re-exported by :
 *  - `@hotjar/voluntary-churn`
 *  - `@hotjar/involuntary-churn`
 * If you extract this out of webapp into a standalone util please inform the FE teams, so they are aware:
 *  - `@hotjar/re-engagement`
 */
export const useUserExperiment = (
  experimentId: string,
  user?: ConstructorParameters<typeof UserTarget>[0] & { id: number },
  canSeeExperiment = true,
): ExperimentProps => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const target = useMemo(() => (user ? new UserTarget(user) : undefined), [user?.id]);
  const experimentData = useExperiment(experimentId, target, canSeeExperiment);

  return experimentData;
};

export const useAnonymousExperiment = (
  experimentId: string,
  anonymousId?: string,
  canSeeExperiment = true,
): ExperimentProps => {
  const target = useMemo(
    () => (anonymousId ? new AnonymousTarget({ id: anonymousId }) : undefined),
    [anonymousId],
  );
  const experimentData = useExperiment(experimentId, target, canSeeExperiment);

  return experimentData;
};

export const useUserWithAnonymousIdExperiment = (
  experimentId: string,
  canSeeExperiment = true,
): ExperimentProps => {
  const { data: user } = useCurrentUser();

  const siteMetadata = useSiteMetadata(true, false);

  const target = useMemo(() => {
    if (!user?.id || !siteMetadata?.finished) return undefined;
    return new UserWithAnonIdTarget({
      user,
      persistentAnonymousId: siteMetadata.persistentAnonymousId || '',
    });
  }, [user, siteMetadata]);
  return useExperiment(experimentId, target, canSeeExperiment);
};

export const useSiteExperiment = (
  experimentId: string,
  site?: Site,
  canSeeExperiment = true,
): ExperimentProps => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const target = useMemo(() => (site ? new SiteTarget(site) : undefined), [site?.id]);
  const experimentData = useExperiment(experimentId, target, canSeeExperiment);

  return experimentData;
};

export const useTrafficExperiment = (
  experimentId: string,
  trafficData: TrafficTargetAttributes,
  canSeeExperiment = true,
): ExperimentProps => {
  const target = useMemo(
    () => (trafficData?.id ? new TrafficCoverageTarget(trafficData) : undefined),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [trafficData?.id],
  );
  const experimentData = useExperiment(experimentId, target, canSeeExperiment);

  return experimentData;
};
